import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { validateDateRange } from '~support-at-home/lib/validate';

import { ErrorResponse } from '~components';
import Field from '~components/form-fields/Field';
import AddModal from '~support-at-home/components/common/AddModal';

import { getAll as getClients } from '~main/actions/clients';
import { getAllSah } from '~support-at-home/actions/fundingTypes';
import { create as createBudget } from '~support-at-home/actions/sahBudgets';

import { clearError } from '~support-at-home/reducers/sahBudgets';

import { selectLookupClients } from '~main/selectors/clients/clients';
import { selectSahFundingTypes } from '~support-at-home/selectors/fundingTypes';
import { selectErrorResponse } from '~support-at-home/selectors/sahBudgets';

interface AddBudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface NewBudget {
  sah_budget_id: string;
  client_id: string;
  funding_source_id: number | null;
  start_date: string;
  end_date: string;
}

const defaultBudget: NewBudget = {
  client_id: '',
  start_date: '',
  funding_source_id: null,
  sah_budget_id: '',
  end_date: '',
};

const AddBudgetModal: React.FC<AddBudgetModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useAppDispatch();
  const errorResponse = useAppSelector(selectErrorResponse);
  const fundingSources = useAppSelector(selectSahFundingTypes);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newBudget, setNewBudget] = useState<NewBudget>(defaultBudget);

  const isAddValid =
    !!newBudget.client_id && !!newBudget.start_date && !!newBudget.end_date && !!newBudget.sah_budget_id;

  // Date validation check for UI feedback
  const hasValidDateRange = validateDateRange({
    start_date: newBudget.start_date,
    end_date: newBudget.end_date,
  });

  // Reset form, load services, and clear errors when modal visibility changes
  useEffect(() => {
    if (isOpen) {
      dispatch(clearError([]));
      dispatch(getClients()); // Preload client data
      setNewBudget(defaultBudget);
      setIsSubmitting(false);
    }
    return () => {
      dispatch(clearError([]));
    };
  }, [isOpen, dispatch]);

  const handleChange = (name: string, value: any) => {
    setNewBudget((prev) => ({ ...prev, [name as keyof NewBudget]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAddValid || isSubmitting) return;

    // Validate date range before submission
    if (!validateDateRange({ start_date: newBudget.start_date, end_date: newBudget.end_date })) {
      dispatch(clearError([]));
      return;
    }

    setIsSubmitting(true);
    dispatch(clearError([]));

    const allowedFields = ['client_id', 'start_date', 'funding_source_id', 'end_date', 'sah_budget_id'];
    const toCreate = {
      ...Object.fromEntries(Object.entries(newBudget).filter(([key]) => allowedFields.includes(key))),
      origin_tag: 'manual',
    };

    try {
      await dispatch(createBudget(toCreate)).unwrap();
      onSuccess();
      onClose();
    } catch (error) {
      // Error will be handled by the reducer and displayed via errorResponse
      setIsSubmitting(false);
    }
  };

  return (
    <AddModal
      isOpen={isOpen}
      title="Add New Budget"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Add Budget"
      submitDisabled={!isAddValid || !hasValidDateRange || isSubmitting}
    >
      <ErrorResponse message={errorResponse} dataCy="add-budget-modal-error" />
      {!hasValidDateRange && newBudget.start_date && newBudget.end_date && (
        <ErrorResponse message="End date must be after start date" dataCy="date-validation-error" />
      )}
      <Field
        id="sah_budget_id"
        caption="SaH Budget ID"
        type="text"
        value={newBudget.sah_budget_id}
        required
        fieldId="sah_budget_id"
        handleChange={handleChange}
      />
      <Field
        id="client_id"
        caption="Client"
        type="select-lazy"
        value={newBudget.client_id}
        selectorOptions={selectLookupClients}
        selectorActions={getClients}
        required
        fieldId="client_id"
        handleChange={handleChange}
      />
      <Field
        id="funding_source_id"
        caption="Funding Source"
        type="select"
        value={newBudget.funding_source_id}
        selectorActions={getAllSah}
        options={fundingSources.map((f: any) => ({ id: f.id, name: f.name }))}
        handleChange={handleChange}
        fieldId="funding_source_id"
        required
      />
      <Field
        id="start_date"
        caption="Start Date"
        type="date"
        value={newBudget.start_date}
        handleChange={handleChange}
        fieldId="start_date"
        validate={(values) => validateDateRange({ start_date: values.start_date, end_date: newBudget.end_date })}
        required
      />
      <Field
        id="end_date"
        caption="End Date"
        type="date"
        value={newBudget.end_date}
        handleChange={handleChange}
        fieldId="end_date"
        validate={(values) => validateDateRange({ start_date: newBudget.start_date, end_date: values.end_date })}
        required
      />
    </AddModal>
  );
};

export default AddBudgetModal;
